// src/context/CartContext.jsx
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartCtx = createContext(null);

// Récupère une propriété possible (color / couleur / option1 / name / label / …)
const pick = (obj, keys) => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).trim()) return String(v).trim();
  }
  return "";
};

// Construit un label lisible à partir de item.variant / item.variantKey / item.variantLabel
export const buildVariantLabel = (item = {}) => {
  if (item.variantLabel && String(item.variantLabel).trim()) return item.variantLabel;

  const v = item.variant || {};
  const color = pick(v, ["color", "couleur", "Colour", "Color", "option1"]);
  const size  = pick(v, ["size", "taille", "Size", "option2"]);
  const name  = pick(v, ["name", "label", "variant", "title"]);

  const parts = [color, size, name].filter(Boolean);
  if (parts.length) return parts.join(" / ");

  const k = item.variantKey;
  if (k && k !== "default" && k !== "|" && String(k).trim()) {
    return String(k).replace(/\|/g, " / ");
  }
  return "";
};

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem("cart") || "[]"); }
    catch { return []; }
  });

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(items));
  }, [items]);

  const addItem = (rawItem) => {
    // normalise & ajoute variantLabel pour l’affichage
    const item = {
      qty: 1,
      ...rawItem,
      variantLabel: buildVariantLabel(rawItem),
    };

    let isNew = false;
    setItems((prev) => {
      const idx = prev.findIndex(
        (p) => p.productId === item.productId && p.variantKey === item.variantKey
      );
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], qty: copy[idx].qty + (item.qty || 1) };
        return copy;
      }
      isNew = true;
      return [...prev, item];
    });
    return { isNew, item };
  };

  const updateQty = (productId, variantKey, qty) => {
    setItems((prev) =>
      prev.map((p) =>
        p.productId === productId && p.variantKey === variantKey
          ? { ...p, qty: Math.max(1, qty) }
          : p
      )
    );
  };

  const removeItem = (productId, variantKey) => {
    setItems((prev) =>
      prev.filter((p) => !(p.productId === productId && p.variantKey === variantKey))
    );
  };

  const clear  = () => setItems([]);
  const total  = useMemo(() => items.reduce((s, it) => s + it.price * it.qty, 0), [items]);
  const value  = { items, addItem, updateQty, removeItem, clear, total };

  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}

export function useCart() {
  const ctx = useContext(CartCtx);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
